/**
 * S6 · CELLS — is the edge real.
 *
 * Not a dashboard page: a DOCUMENT WITH LIVE NUMBERS EMBEDDED, because "is
 * the edge real" is answered by an argument, not a tile.
 *
 * Three rules make it load-bearing rather than decorative:
 *
 *  1. **Live is scored against HONEST, never PUBLISHED.** The published cell
 *     is shown only as the thing being explained, struck through as
 *     SUPERSEDED wherever it is quoted.
 *  2. **The n-gate.** Below the conclusion threshold the surface refuses.
 *     A surface that lets n=3 look like evidence is worse than no surface.
 *     The RULE is stated ONCE, in the lede, in the server's own words; each
 *     cell then carries the short refusal token with the full sentence on
 *     its title. Six repetitions of one sentence is not six caveats, it is
 *     one caveat the reader has learned to skip. There is deliberately no
 *     "trades to 1σ" progress bar (D15): it assumes iid draws from a fixed
 *     distribution, and these returns are fat-tailed, regime-dependent and
 *     correlated through one macro driver.
 *  3. **The rot guard.** Past 30 days the whole surface goes amber-free but
 *     stamped: `L6 has not run in N days — these are historical claims`.
 *     (Amber is reserved for exposure; the rot stamp is a loud grey.)
 *
 * The SCOPE block is rendered VERBATIM from the server, in the body face at a
 * readable measure — and COLLAPSED. Paraphrasing a caveat is how a caveat
 * dies; four lines of English above the first number is a caveat nobody
 * reads either. It is one click away and unabridged.
 *
 * THE BRACKET moves the LIVE arm and nothing else. A claim is a research fact
 * about the system, made at a fixed window that is printed beside it; letting
 * the reader's range silently re-cut a published claim would make the ladder
 * mean a different thing every time the control moved.
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import { BracketBar } from "../lib/BracketBar";
import { refetchMsFor, scopeParam, useRange } from "../lib/bracket";
import { navigate } from "../lib/route";
import { ageSince } from "../lib/time";

const ARM_LABEL: Record<string, string> = {
  published: "PUBLISHED",
  replay_parity: "REPLAYED",
  replay_honest: "HONEST",
  live: "LIVE",
};

/** The refusal arrives as one sentence: `n < 20 — this surface will not draw
 *  a conclusion below its threshold`. The head is the token, the whole
 *  sentence stays on the title. Derived from the server's string — the
 *  threshold is never hardcoded here. */
function refusalToken(refusal: string | null | undefined): string {
  if (!refusal) return "no verdict";
  const head = String(refusal).split("—")[0]!.trim();
  return head || String(refusal);
}

export function Cells() {
  const range = useRange();
  // The LIVE arm moves with the reader's bracket. CLAIMS do not: a claim is a
  // research fact about the SYSTEM and carries its own window column.
  const q = useQuery({
    queryKey: ["cells", range.since, range.until],
    queryFn: () => api.cells({ since: range.since, until: range.until, scope_label: scopeParam(range) }),
    refetchInterval: refetchMsFor(range),
  });
  const g = q.data?.global;
  const ladder: any[] = g?.ladder ?? [];
  const cells: any[] = q.data?.cells ?? [];
  const newestRun = ladder.reduce<string | null>((acc, c) => (c.run_at && (!acc || c.run_at > acc) ? c.run_at : acc), null);
  const rotDays = newestRun ? (Date.now() - Date.parse(newestRun)) / 86400000 : null;
  // Stated once, in the server's own words, instead of once per cell.
  const gateRule = cells.find((c: any) => c.live?.verdict_refusal)?.live?.verdict_refusal ?? null;

  return (
    <section className="surface cells">
      <header className="surface__head">
        <h1 className="surface__title">The edge</h1>
        <div className="surface__meta mono">
          {cells.length} cells · {ladder.length} claim{ladder.length === 1 ? "" : "s"}
        </div>
      </header>

      <BracketBar />

      {gateRule && <p className="surface__lede">{gateRule}</p>}

      <div className="surface__body">
        {rotDays !== null && rotDays > 30 && (
          <div className="callout callout--stale">
            L6 has not run in {Math.floor(rotDays)} days — these are historical claims, not a current measurement.
          </div>
        )}

        {ladder.length === 0 && (
          <div
            className="stub"
            title="claims come from an L6 run, which is offline by charter — nothing populates them yet"
          >
            no claims ingested · L6 offline
          </div>
        )}

        {ladder.length > 0 && (
          <div className="table">
            <table className="ladder mono">
              <thead>
                <tr>
                  <th>arm</th>
                  <th className="num">cum</th>
                  <th className="num">n</th>
                  <th title="the claim's own measurement window — fixed at the run, and not the bracket above">
                    window
                  </th>
                  <th>run</th>
                </tr>
              </thead>
              <tbody>
                {ladder.map((c) => (
                  <tr key={c.arm} className={c.superseded ? "superseded" : ""}>
                    <td>{ARM_LABEL[c.arm] ?? c.arm}</td>
                    <td className={`num${c.value_pct === null ? "" : c.value_pct > 0 ? " up" : c.value_pct < 0 ? " down" : ""}`}>
                      {c.value_pct === null ? "—" : `${c.value_pct > 0 ? "+" : ""}${c.value_pct}%`}
                    </td>
                    <td className="num dim">{c.n ?? "—"}</td>
                    <td className="dim">{c.window ?? "—"}</td>
                    <td className="dim">{c.run_at ? ageSince(c.run_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="note">claims carry their own window and do not move with the bracket. LIVE does.</p>
          </div>
        )}

        {/* Verbatim, and collapsed. Paraphrasing a caveat is how a caveat
            dies; burying it is not — and four lines of English above the
            first number is four lines nobody reads. */}
        <details className="disclose scope-block">
          <summary>SCOPE — read before quoting anything above</summary>
          <p className="scope-block__text">{g?.scope}</p>
          <p className="note">→ {g?.rule}</p>
        </details>

        {cells.map((c: any) => (
          <Cell key={c.cell_id} cell={c} />
        ))}
      </div>
    </section>
  );
}

function Cell({ cell }: { cell: any }) {
  const live = cell.live ?? {};
  const [source, instrument, direction] = String(cell.cell_id).split("/");
  const watchdog = cell.watchdog_json ? safeJson(cell.watchdog_json) : null;
  const claims: any[] = cell.claims ?? [];
  const cum = live.cum_pct;
  return (
    <section className="cell">
      <div className="cell__head">
        <span className="cell__id mono">{cell.cell_id}</span>
        {cell.toggle_name && (
          <button
            className="linkish"
            onClick={() => navigate({ name: "gates" })}
            title="the same write, mirrored beside the evidence it governs"
          >
            gate {cell.toggle_name}
          </button>
        )}
        <button
          className="linkish"
          onClick={() => navigate({ name: "cell", source: source!, instrument: instrument!, direction: direction! })}
        >
          open cell
        </button>
      </div>

      {claims.length > 0 && (
        <div className="band">
          <div className="band__title">CLAIMS</div>
          <div className="figs">
            {claims.map((c: any) => (
              <div key={c.arm} className="fig">
                <span className="fig__k">
                  {ARM_LABEL[c.arm] ?? c.arm}
                  {c.superseded ? " · SUPERSEDED" : ""}
                </span>
                <span className={`fig__v mono num${c.superseded ? " superseded" : ""}`}>
                  {c.value_pct === null ? "—" : `${c.value_pct}%`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="figs">
        <div className="fig" title={live.window ? `LIVE arm, ${live.window}` : "LIVE arm"}>
          <span className="fig__k">n attested</span>
          <span className="fig__v mono num">{live.n_attested ?? 0}</span>
        </div>
        {/* The exclusion rule is STATED ONCE, here, on the figure it governs —
            not once per cell, eleven times down the page. */}
        <div
          className="fig"
          title={`compounded product of (1 + pnl_pct) over attested closes${live.window ? ` in ${live.window}` : ""} — estimated and pending closes are listed beside it, never folded in${live.lower_bound ? " · series capped: this is a lower bound" : ""}`}
        >
          <span className="fig__k">cum</span>
          <span className={`fig__v mono num${cum === null || cum === undefined ? "" : cum > 0 ? " up" : cum < 0 ? " down" : ""}`}>
            {cum === null || cum === undefined ? "—" : `${cum}%`}
          </span>
        </div>
        <div className="fig">
          <span className="fig__k">win</span>
          <span className="fig__v mono num">
            {live.wins ?? 0}/{live.n_attested ?? 0}
          </span>
        </div>
        <div className="fig">
          <span className="fig__k">estimated</span>
          <span className="fig__v mono num">{live.n_estimated ?? 0}</span>
        </div>
        <div className="fig">
          <span className="fig__k">pending</span>
          <span className="fig__v mono num">{live.n_pending ?? 0}</span>
        </div>
      </div>

      <div className="verdict-line">
        <span className="verdict">VERDICT</span>
        {live.verdict ? (
          <span className="verdict">{live.verdict}</span>
        ) : (
          <span className="chip chip--down" title={live.verdict_refusal ?? undefined}>
            {refusalToken(live.verdict_refusal)}
          </span>
        )}
      </div>

      {watchdog && (
        <p className="note">
          <span className="mono">WATCHDOG</span> {watchdog.rule ?? JSON.stringify(watchdog)}{" "}
          {typeof watchdog.week === "number" && typeof watchdog.weeks === "number" && (
            <span className="watchdog">
              {Array.from({ length: Math.max(0, watchdog.weeks) }, (_, i) => (
                <i key={i} className={`watchdog__seg${i < watchdog.week ? " watchdog__seg--done" : ""}`} />
              ))}{" "}
              week {watchdog.week} of {watchdog.weeks}
            </span>
          )}
        </p>
      )}
    </section>
  );
}

function safeJson(s: string): any {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

export function CellView({ source, instrument, direction }: { source: string; instrument: string; direction: string }) {
  const q = useQuery({
    queryKey: ["cell", source, instrument, direction],
    queryFn: () => api.cell(source, instrument, direction),
  });
  if (q.isLoading) return <div className="stub">…</div>;
  if (q.error) return <div className="stub stub--error">no such cell in this tenant</div>;
  return (
    <section className="surface">
      <header className="surface__head">
        <h1 className="surface__title">Cell</h1>
        <div className="surface__meta mono">{q.data.cell_id}</div>
      </header>
      <div className="surface__body">
        <Cell
          cell={{
            ...(q.data.config ?? {}),
            cell_id: q.data.cell_id,
            claims: q.data.claims,
            live: q.data.live,
          }}
        />
        {q.data.config && (
          <details className="disclose">
            <summary>config</summary>
            <pre className="mono raw__pre">{JSON.stringify(q.data.config, null, 2)}</pre>
          </details>
        )}
      </div>
    </section>
  );
}
